import React, { useState } from "react";
import { Download, Lock, FileText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { log } from "../lib/log";
import { useAuth } from "../lib/auth";
import { usePlanCheckout } from "./pricing/usePlanCheckout";
import BlueprintPreview from "./BlueprintPreview";

const PAID = new Set(["blueprint", "solar_return", "infinity"]);
export const ownsBlueprint = (user) => Boolean(user?.vip_lifetime) || PAID.has(user?.tier);

// One banner for Dashboard + Natal Reveal: buyers download the 21-page PDF, others unlock it for $4.40.
export default function BlueprintBanner({ className = "", testid = "blueprint-banner" }) {
  const { user } = useAuth();
  const { busy, purchase } = usePlanCheckout();
  const [downloading, setDownloading] = useState(false);
  const owned = ownsBlueprint(user);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/users/natal-blueprint.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `StarGrid-Blueprint-${(user?.natal?.name || "traveler").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success("Your 21-page Blueprint is downloading ✨");
    } catch (e) {
      log.error("Blueprint PDF download failed:", e);
      toast.error(e?.response?.status === 402 ? "Unlock the Blueprint for $4.40 to download it" : "Could not generate the PDF. Try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className={`relative overflow-hidden rounded-xl p-4 sm:p-5 border border-[#C8A96E]/50 ${className}`}
      style={{ background: "linear-gradient(135deg, #1C1622 0%, #4A3356 55%, #6A4F78 100%)" }}
      data-testid={testid}
    >
      <div className="absolute -right-6 -top-8 w-40 h-40 rounded-full opacity-40 blur-3xl bg-[#C8A96E]" />
      <div className="relative flex flex-col md:flex-row md:items-center gap-4 justify-between">
        <div>
          <div className="text-[9px] uppercase tracking-[0.3em] text-[#C8A96E] flex items-center gap-1.5">
            <FileText size={11} /> Your Deep Celestial Blueprint
          </div>
          <div className="font-heading text-xl sm:text-2xl mt-1">
            {owned ? "Download your 21-page Natal Blueprint" : "Unlock your 21-page Natal Blueprint"}
          </div>
          <p className="text-xs text-[#E8DFD3] mt-1.5 max-w-xl">
            Birth signature (Sun · Moon · Rising · Venus · Jupiter) · 9 planetary line chapters · top Money, Love &amp; Healing cities · 8-day wellness journey · 12-month outlook.
            {owned ? " Yours forever — also emailed to you." : " One-time $4.40 · emailed as PDF the moment you pay."}
          </p>
        </div>
        {owned ? (
          <Button
            data-testid="blueprint-download-btn"
            onClick={download}
            disabled={downloading || !user?.natal}
            className="bg-[#C8A96E] text-[#15111A] hover:bg-[#A98B55] rounded-none h-10 px-6 uppercase tracking-widest text-[11px] font-bold shadow-[0_0_20px_rgba(200,169,110,0.4)] flex items-center gap-2 self-start md:self-center"
          >
            <Download size={14} />
            {downloading ? "Preparing PDF…" : "Download Blueprint PDF"}
          </Button>
        ) : (
          <Button
            data-testid="blueprint-unlock-btn"
            onClick={() => purchase("natal_blueprint")}
            disabled={busy === "natal_blueprint" || !user?.natal}
            className="bg-[#C8A96E] text-[#15111A] hover:bg-[#A98B55] rounded-none h-10 px-6 uppercase tracking-widest text-[11px] font-bold shadow-[0_0_20px_rgba(200,169,110,0.4)] flex items-center gap-2 self-start md:self-center"
          >
            <Lock size={14} />
            {busy === "natal_blueprint" ? "Opening checkout…" : "Unlock for $4.40"}
          </Button>
        )}
      </div>
      {!owned && user?.natal && <BlueprintPreview onUnlock={() => purchase("natal_blueprint")} busy={busy === "natal_blueprint"} />}
    </div>
  );
}
